import { useCallback, useRef, useState } from 'react';

import { VideoComposition, normalizeComposition } from './composition';

const LIMIT = 80;
/** Changes with the same merge key this close together are one undo step (a slider drag). */
const MERGE_WINDOW_MS = 700;

export interface CompositionHistory {
    comp: VideoComposition;
    /** Commit a change. `merge` coalesces a burst of edits to one property into one undo step. */
    commit: (next: VideoComposition | ((prev: VideoComposition) => VideoComposition), merge?: string) => void;
    /** Show a state without an undo step (a live drag frame; `commit` at the end). */
    preview: (next: VideoComposition) => void;
    /** The last committed state (what a drag started from). */
    committed: () => VideoComposition;
    undo: () => void;
    redo: () => void;
    canUndo: boolean;
    canRedo: boolean;
    reset: (comp: VideoComposition) => void;
}

/**
 * The editor's composition plus undo/redo.
 *
 * A timeline drag fires dozens of updates; `preview` shows them without
 * filling the history, and the drag's end `commit`s once against the state
 * the drag began from. History lives in refs so updates stay pure.
 */
export function useCompositionHistory(initial: VideoComposition): CompositionHistory {
    const [comp, setComp] = useState<VideoComposition>(() => normalizeComposition(initial));
    const [, setVersion] = useState(0);
    const past = useRef<VideoComposition[]>([]);
    const future = useRef<VideoComposition[]>([]);
    const current = useRef<VideoComposition>(comp);
    const lastMerge = useRef<{ key: string; at: number } | null>(null);
    const bump = () => setVersion((v) => v + 1);

    const commit = useCallback((next: VideoComposition | ((prev: VideoComposition) => VideoComposition), merge?: string) => {
        const base = current.current;
        const value = normalizeComposition(typeof next === 'function' ? (next as (p: VideoComposition) => VideoComposition)(base) : next);
        const now = Date.now();
        const coalesce = !!merge && lastMerge.current?.key === merge && now - lastMerge.current.at < MERGE_WINDOW_MS;
        lastMerge.current = merge ? { key: merge, at: now } : null;
        if (!coalesce) past.current = [...past.current.slice(-(LIMIT - 1)), base];
        future.current = [];
        current.current = value;
        setComp(value);
        bump();
    }, []);

    const preview = useCallback((next: VideoComposition) => {
        setComp(normalizeComposition(next));
    }, []);

    const undo = useCallback(() => {
        if (!past.current.length) return;
        const prev = past.current[past.current.length - 1];
        past.current = past.current.slice(0, -1);
        future.current = [current.current, ...future.current].slice(0, LIMIT);
        current.current = prev;
        lastMerge.current = null;
        setComp(prev);
        bump();
    }, []);

    const redo = useCallback(() => {
        if (!future.current.length) return;
        const [next, ...rest] = future.current;
        future.current = rest;
        past.current = [...past.current, current.current].slice(-LIMIT);
        current.current = next;
        lastMerge.current = null;
        setComp(next);
        bump();
    }, []);

    const reset = useCallback((value: VideoComposition) => {
        const n = normalizeComposition(value);
        current.current = n;
        past.current = [];
        future.current = [];
        setComp(n);
        bump();
    }, []);

    const committed = useCallback(() => current.current, []);

    return {
        comp,
        commit,
        preview,
        committed,
        undo,
        redo,
        canUndo: past.current.length > 0,
        canRedo: future.current.length > 0,
        reset,
    };
}
